import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { getKeyCode } from 'state/secondaryMarket/utils';
import type { AppState } from 'state/state.model';
import { getProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';

import type { AstroProductLoadingStatus, AstroProductStatus } from './astroProduct.model';

export function getAstroProductState(state: AppState, isin: string) {
  return state.secondaryMarket.referential.astroProduct[isin];
}

function getAstroProductData(state: AppState, isin: string) {
  return getAstroProductState(state, isin)?.data;
}

export function getSecondaryMarketRealtimeAstroProductFromTab(state: AppState, tabId: string) {
  const rowId = getProductKey(state, tabId);
  const isin = getKeyCode(rowId);
  return isin !== undefined ? getAstroProductState(state, isin) : undefined;
}

export function getSecondaryMarketRealtimeAstroProducLifecycleUrl(state: AppState, tabId: string) {
  const astroProductState = getSecondaryMarketRealtimeAstroProductFromTab(state, tabId);
  if (astroProductState === undefined || astroProductState?.isLoading || astroProductState?.hasError) {
    return null;
  }
  return astroProductState.data?.lifecycleUrl ?? null;
}

export function isSecondaryMarketAstroProductNotLoaded(appState: AppState, isin: string) {
  const state = getAstroProductState(appState, isin);
  return state === undefined || state.hasError;
}

export function isSecondaryMarketAstroProductLoading(appState: AppState, isin: string) {
  const state = getAstroProductState(appState, isin);
  return state?.isLoading;
}

export function getSecondaryMarketAstroProductStatus(
  appState: AppState,
  isin: string | undefined,
): AstroProductStatus | AstroProductLoadingStatus | null {
  if (isin === undefined) {
    return 'Missing';
  }
  const state = getAstroProductState(appState, isin);
  if (state === undefined) {
    return null;
  }
  if (state.isLoading) {
    return 'Loading';
  }
  if (state.hasError) {
    return 'Error';
  }
  if (state.data === null) {
    return 'Missing';
  }
  if (state.data.isActive === true) {
    return 'Active';
  }
  if (state.data.isActive === false) {
    return 'NotActive';
  }
  return 'Loading' as never;
}

export function getSecondaryMarketAstroProductStatusFromTab(appState: AppState, tabId: string) {
  const product = getSecondaryMarketRealtimeAstroProductFromTab(appState, tabId);
  return getSecondaryMarketAstroProductStatus(appState, product?.data?.isin);
}
export function getSecondaryMarketAstroProductId(appState: AppState, isin: string) {
  return getAstroProductData(appState, isin)?.productId;
}
export function getSecondaryMarketAstroProductIdType(appState: AppState, isin: string) {
  return getAstroProductData(appState, isin)?.productIdType;
}
export function getSecondaryMarketAstroProductIdSource(appState: AppState, isin: string) {
  return getAstroProductData(appState, isin)?.productIdSource;
}

export function getSecondaryMarketAstroProductClients(appState: AppState, isin: string): ISalesCounterpart[] {
  return getAstroProductData(appState, isin)?.clients ?? [];
}
export function getSecondaryMarketRealtimeAstroProductDenominationFromTab(appState: AppState, tabId: string) {
  return getSecondaryMarketRealtimeAstroProductFromTab(appState, tabId)?.data?.denomination;
}

export function getSecondaryMarketRealtimeAstroProductIsinFromTab(appState: AppState, tabId: string) {
  return getSecondaryMarketRealtimeAstroProductFromTab(appState, tabId)?.data?.isin;
}
